// ── useNotificationCounts.js ──────────────────────────────────────────────────
// Hook مركزي لإدارة عدادات الإشعارات في كل أنحاء الموقع
//
// يتابع في الوقت الفعلي:
//  - رسائل المحادثات الخاصة غير المقروءة (privateChat)
//  - طلبات الصداقة الواردة (friendRequests)
//  - إشعارات RTDB غير المقروءة للمستخدم (userNotifs)
//  - تذاكر الدعم غير المردود عليها — للأدمن (support)
//  - الشكاوي غير المردود عليها — للأدمن (complaints)
//  - إثباتات الدفع المعلقة — للأدمن (payments)
//  - ردود الدعم/الشكاوي على المستخدم — للمستخدم (supportReplies)
//
// منطق "السين" الذكي:
//  - يتم mark as seen عند استدعاء markSeen(section)
//  - يُحفظ lastSeenAt في localStorage
//  - العداد يختفي تلقائياً بعد فتح القسم، ويعود عند وصول رسائل جديدة
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState, useCallback } from "react";
import { ref, onValue, off } from "firebase/database";
import { db } from "../firebase";
import { isOwnerUser } from "../utils/verifiedAccounts";

// ── مفاتيح localStorage لحفظ آخر وقت مشاهدة ─────────────────────────────────
const LS_PREFIX = "notif_seen_";
const getSeenKey  = (section) => `${LS_PREFIX}${section}`;
const getLastSeen = (section) => {
  try { return parseInt(localStorage.getItem(getSeenKey(section)) || "0", 10); } catch { return 0; }
};
const saveLastSeen = (section) => {
  try { localStorage.setItem(getSeenKey(section), String(Date.now())); } catch {}
};

// ── الـ Hook الرئيسي ──────────────────────────────────────────────────────────
const useNotificationCounts = (currentUser) => {
  const uid     = currentUser?.uid || null;
  const isGuest = !uid || currentUser?.isAnonymous;
  const isOwner = uid ? isOwnerUser(uid) : false;

  const [counts, setCounts] = useState({
    privateChat:    0,   // رسائل خاصة غير مقروءة
    friendRequests: 0,   // طلبات صداقة واردة
    userNotifs:     0,   // إشعارات RTDB للمستخدم
    support:        0,   // تذاكر دعم جديدة (أدمن)
    complaints:     0,   // شكاوي جديدة (أدمن)
    payments:       0,   // دفعات معلقة (أدمن)
    supportReplies: 0,   // ردود دعم/شكاوي على المستخدم
  });

  const seenRef = useRef({
    privateChat:    getLastSeen("privateChat"),
    friendRequests: getLastSeen("friendRequests"),
    userNotifs:     getLastSeen("userNotifs"),
    support:        getLastSeen("support"),
    complaints:     getLastSeen("complaints"),
    payments:       getLastSeen("payments"),
    supportReplies: getLastSeen("supportReplies"),
  });

  // ── مساعد لتحديث عداد واحد ──────────────────────────────────────────────────
  const setCount = useCallback((key, val) => {
    setCounts((prev) => {
      if (prev[key] === val) return prev;
      return { ...prev, [key]: val };
    });
  }, []);

  // ════════════════════════════════════════════════════════════════════════════
  // 1. رسائل المحادثات الخاصة غير المقروءة
  // ════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (isGuest || !uid) { setCount("privateChat", 0); return; }

    // نستمع لـ /private_chats — كل محادثة تحتوي على messages
    // نحسب الرسائل التي:
    //   - senderId !== uid (لم أرسلها أنا)
    //   - timestamp > lastSeen
    const chatsRef = ref(db, `private_chats`);
    const unsub = onValue(chatsRef, (snap) => {
      if (!snap.exists()) { setCount("privateChat", 0); return; }
      const allChats = snap.val();
      let total = 0;
      const lastSeen = seenRef.current.privateChat;

      Object.entries(allChats).forEach(([chatId, chatData]) => {
        // الـ chatId هو uid1_uid2 — نتحقق أن uid موجود فيه
        if (!chatId.includes(uid)) return;
        const messages = chatData?.messages;
        if (!messages) return;
        Object.values(messages).forEach((msg) => {
          if (!msg) return;
          if (msg.senderId === uid) return;        // رسالتي أنا
          if (!msg.timestamp) return;
          if (msg.timestamp <= lastSeen) return;   // رآها سابقاً
          total++;
        });
      });

      setCount("privateChat", total);
    });

    return () => off(chatsRef);
  }, [uid, isGuest, setCount]);

  // ════════════════════════════════════════════════════════════════════════════
  // 2. طلبات الصداقة الواردة
  // ════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (isGuest || !uid) { setCount("friendRequests", 0); return; }

    const reqRef = ref(db, `friend_requests/received/${uid}`);
    const unsub  = onValue(reqRef, (snap) => {
      if (!snap.exists()) { setCount("friendRequests", 0); return; }
      const data      = snap.val();
      const lastSeen  = seenRef.current.friendRequests;
      const newCount  = Object.values(data).filter((r) =>
        r && r.sentAt && r.sentAt > lastSeen
      ).length;
      setCount("friendRequests", newCount);
    });

    return () => off(reqRef);
  }, [uid, isGuest, setCount]);

  // ════════════════════════════════════════════════════════════════════════════
  // 3. إشعارات RTDB للمستخدم (غير المقروءة)
  // ════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (isGuest || !uid) { setCount("userNotifs", 0); return; }

    const notifRef = ref(db, `notifications/${uid}/push`);
    const unsub    = onValue(notifRef, (snap) => {
      if (!snap.exists()) { setCount("userNotifs", 0); return; }
      const data  = snap.val();
      const count = Object.values(data).filter(
        (n) => n && !n.read
      ).length;
      setCount("userNotifs", count);
    });

    return () => off(notifRef);
  }, [uid, isGuest, setCount]);

  // ════════════════════════════════════════════════════════════════════════════
  // 4 & 5 & 6. تذاكر الدعم + الشكاوي + الدفعات — للأدمن فقط
  // ════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!isOwner) {
      setCount("support",    0);
      setCount("complaints", 0);
      setCount("payments",   0);
      return;
    }

    // ── الدعم ──────────────────────────────────────────────────────────────
    const supportRef = ref(db, "supportTickets");
    const unsubSupport = onValue(supportRef, (snap) => {
      if (!snap.exists()) { setCount("support", 0); return; }
      const data  = snap.val();
      const lastSeen = seenRef.current.support;
      const count = Object.values(data).filter(
        (t) => t && t.status === "pending" && !t.adminRead && t.createdAt && t.createdAt > lastSeen
      ).length;
      setCount("support", count);
    });

    // ── الشكاوي ─────────────────────────────────────────────────────────────
    const complaintsRef = ref(db, "complaints");
    const unsubComplaints = onValue(complaintsRef, (snap) => {
      if (!snap.exists()) { setCount("complaints", 0); return; }
      const data  = snap.val();
      const lastSeen = seenRef.current.complaints;
      const count = Object.values(data).filter(
        (c) => c && c.status === "pending" && !c.adminRead && c.createdAt && c.createdAt > lastSeen
      ).length;
      setCount("complaints", count);
    });

    // ── الدفعات ─────────────────────────────────────────────────────────────
    const paymentsRef = ref(db, "pendingPayments");
    const unsubPayments = onValue(paymentsRef, (snap) => {
      if (!snap.exists()) { setCount("payments", 0); return; }
      const data  = snap.val();
      const lastSeen = seenRef.current.payments;
      const count = Object.values(data).filter(
        (p) => p && p.status === "pending" && p.createdAt && p.createdAt > lastSeen
      ).length;
      setCount("payments", count);
    });

    return () => {
      off(supportRef);
      off(complaintsRef);
      off(paymentsRef);
    };
  }, [isOwner, uid, setCount]);

  // ════════════════════════════════════════════════════════════════════════════
  // 7. ردود الدعم/الشكاوي على المستخدم (supportReplies)
  //    نجمع ردود التذاكر + ردود الشكاوي معاً في عداد واحد
  // ════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (isGuest || !uid || isOwner) { setCount("supportReplies", 0); return; }

    // نستخدم ref لتخزين العددين ونجمعهما
    let ticketsCount    = 0;
    let complaintsCount = 0;

    const userTicketsRef = ref(db, "supportTickets");
    const unsubTickets   = onValue(userTicketsRef, (snap) => {
      const lastSeen = seenRef.current.supportReplies;
      if (!snap.exists()) { ticketsCount = 0; }
      else {
        const data = snap.val();
        ticketsCount = Object.values(data).filter(
          (t) =>
            t &&
            t.userId    === uid &&
            t.status    === "answered" &&
            t.answeredAt &&
            t.answeredAt > lastSeen &&
            !t.userRead
        ).length;
      }
      setCount("supportReplies", ticketsCount + complaintsCount);
    });

    const userComplaintsRef = ref(db, "complaints");
    const unsubComplaints   = onValue(userComplaintsRef, (snap) => {
      const lastSeen = seenRef.current.supportReplies;
      if (!snap.exists()) { complaintsCount = 0; }
      else {
        const data = snap.val();
        complaintsCount = Object.values(data).filter(
          (c) =>
            c &&
            c.userId    === uid &&
            c.status    === "answered" &&
            c.answeredAt &&
            c.answeredAt > lastSeen &&
            !c.userRead
        ).length;
      }
      setCount("supportReplies", ticketsCount + complaintsCount);
    });

    return () => {
      off(userTicketsRef);
      off(userComplaintsRef);
    };
  }, [uid, isGuest, isOwner, setCount]);

  // ════════════════════════════════════════════════════════════════════════════
  // markSeen — يُسجّل "رأى" القسم ويُصفّر العداد
  // ════════════════════════════════════════════════════════════════════════════
  const markSeen = useCallback((section) => {
    saveLastSeen(section);
    seenRef.current[section] = Date.now();
    setCount(section, 0);
  }, [setCount]);

  // ── إجمالي الإشعارات للمستخدم العادي ───────────────────────────────────────
  const totalUser = counts.privateChat + counts.friendRequests +
                    counts.userNotifs  + counts.supportReplies;

  // ── إجمالي إشعارات الأدمن ───────────────────────────────────────────────────
  const totalAdmin = counts.support + counts.complaints + counts.payments;

  // ── العدد الكلي الظاهر في زر Chat بالـ Navbar ───────────────────────────────
  const chatTotal = counts.privateChat + counts.friendRequests;

  return {
    counts,
    markSeen,
    totalUser,
    totalAdmin,
    chatTotal,
    isOwner,
  };
};

export default useNotificationCounts;
